import { AlertTriangle, Building2, FileText, Mail, Percent, Warehouse } from "lucide-react";
import { Controller, UseFormReturn } from "react-hook-form";
import { Button } from "../../../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../../components/ui/dialog";
import { Input } from "../../../../../components/ui/input";
import { Label } from "../../../../../components/ui/label";
import { Switch } from "../../../../../components/ui/switch";
import { ImileFranquia } from "../types";
import {
  ImileFranquiaEditFormData,
  previewOpsDerivadas,
} from "../utils/validation-schemas";

interface EditFranquiaDialogProps {
  open: boolean;
  franquia: ImileFranquia | null;
  form: UseFormReturn<ImileFranquiaEditFormData, any, ImileFranquiaEditFormData>;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
}

export function EditFranquiaDialog({
  open,
  franquia,
  form,
  onSave,
  onCancel,
  isSaving,
}: EditFranquiaDialogProps) {
  const watchApelido = form.watch("apelido");
  const watchDs = form.watch("ds");
  const opsPreview = previewOpsDerivadas(watchApelido, watchDs);

  // Mudar prefixo ou DS troca as ops da franquia, e o romaneio já gravado fica
  // na ops antiga. O aviso só aparece quando é esse o caso.
  const opsAtuais = franquia?.opsDerivadas ?? [];
  const opsMudaram =
    opsPreview.length > 0 &&
    (opsPreview.length !== opsAtuais.length ||
      opsPreview.some((ops) => !opsAtuais.includes(ops)));

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Franquia iMile - {franquia?.razaoSocial}</DialogTitle>
        </DialogHeader>

        <form onSubmit={onSave} className="space-y-6">
          <div className="p-4 rounded-lg bg-muted/50">
            <h3 className="text-sm font-medium text-muted-foreground mb-3">
              Informações da Empresa
            </h3>
            <div className="space-y-2">
              <div>
                <span className="text-sm font-semibold">CNPJ:</span>{" "}
                <span className="text-sm">{franquia?.cnpj}</span>
              </div>
              <div>
                <span className="text-sm font-semibold">Razão Social:</span>{" "}
                <span className="text-sm">{franquia?.razaoSocial}</span>
              </div>
              <div>
                <span className="text-sm font-semibold">Ops atuais:</span>{" "}
                <span className="font-mono text-sm">
                  {opsAtuais.length > 0 ? opsAtuais.join(", ") : "-"}
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Controller
              name="apelido"
              control={form.control}
              render={({ field, fieldState }) => (
                <div className="space-y-2">
                  <Label htmlFor="edit-apelido">Apelido / prefixo</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="edit-apelido"
                      placeholder="Ex: realog"
                      {...field}
                      className="pl-9"
                    />
                  </div>
                  {fieldState.error && (
                    <p className="text-sm font-medium text-destructive">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}
            />

            <Controller
              name="ds"
              control={form.control}
              render={({ field, fieldState }) => (
                <div className="space-y-2">
                  <Label htmlFor="edit-ds">DS / bases</Label>
                  <div className="relative">
                    <Warehouse className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="edit-ds"
                      placeholder="Ex: tji,lms"
                      {...field}
                      className="pl-9"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Separadas por vírgula.
                  </p>
                  {fieldState.error && (
                    <p className="text-sm font-medium text-destructive">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}
            />
          </div>

          {opsPreview.length > 0 && (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
              <p className="text-sm font-medium text-foreground">
                Ops depois de salvar
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {opsPreview.map((ops) => (
                  <span
                    key={ops}
                    className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-1 font-mono text-xs font-medium text-primary"
                  >
                    {ops}
                  </span>
                ))}
              </div>
            </div>
          )}

          {opsMudaram && (
            <div className="flex gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <div className="text-sm text-amber-900">
                <p className="font-semibold">As ops desta franquia vão mudar.</p>
                <p className="mt-1">
                  Os romaneios já gravados continuam na ops antiga e deixam de
                  aparecer no checkout. Para trazê-los, é preciso rodar os
                  UPDATEs de re-derivação da migration
                  <span className="font-mono">
                    {" "}
                    migration_romaneio_imile_franquia.sql
                  </span>
                  .
                </p>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <Controller
              name="emailContato"
              control={form.control}
              render={({ field, fieldState }) => (
                <div className="space-y-2">
                  <Label htmlFor="edit-emailContato">Email(s) de Contato</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="edit-emailContato"
                      type="text"
                      placeholder="financeiro@empresa.com.br | outro@empresa.com.br"
                      {...field}
                      className="pl-9"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Para múltiplos emails, separe com | (pipe).
                  </p>
                  {fieldState.error && (
                    <p className="text-sm font-medium text-destructive">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}
            />

            <Controller
              name="franquiaUuid"
              control={form.control}
              render={({ field, fieldState }) => (
                <div className="space-y-2">
                  <Label htmlFor="edit-franquiaUuid">Franquia UUID</Label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="edit-franquiaUuid"
                      placeholder="8d03027b-5236-4533-8e9e-..."
                      {...field}
                      className="pl-9"
                    />
                  </div>
                  {fieldState.error && (
                    <p className="text-sm font-medium text-destructive">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}
            />

            <Controller
              name="redutor"
              control={form.control}
              render={({ field, fieldState }) => (
                <div className="space-y-2">
                  <Label htmlFor="edit-redutor">Redutor</Label>
                  <div className="relative">
                    <Percent className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="edit-redutor"
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      placeholder="1.00"
                      {...field}
                      onChange={(e) =>
                        field.onChange(e.target.valueAsNumber || e.target.value)
                      }
                      className="pl-9"
                    />
                  </div>
                  {fieldState.error && (
                    <p className="text-sm font-medium text-destructive">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}
            />

            <Controller
              name="nfsAtivo"
              control={form.control}
              render={({ field }) => (
                <div className="flex items-center justify-between p-4 rounded-lg border bg-background">
                  <Label
                    htmlFor="edit-nfsAtivo"
                    className="text-base cursor-pointer"
                  >
                    NFS (Nota Fiscal de Serviço) Ativo
                  </Label>
                  <Switch
                    id="edit-nfsAtivo"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </div>
              )}
            />

            <Controller
              name="nfsSrAtivo"
              control={form.control}
              render={({ field }) => (
                <div className="flex items-center justify-between p-4 rounded-lg border bg-background">
                  <Label
                    htmlFor="edit-nfsSrAtivo"
                    className="text-base cursor-pointer"
                  >
                    NFS Saldo Remanescente Ativo
                  </Label>
                  <Switch
                    id="edit-nfsSrAtivo"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </div>
              )}
            />
          </div>
        </form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSaving}
          >
            CANCELAR
          </Button>
          <Button type="button" onClick={onSave} disabled={isSaving}>
            {isSaving ? "Atualizando..." : "ATUALIZAR"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
